import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Switch,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RepeatIcon from '@mui/icons-material/Repeat';
import api from './axiosConfig';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const EmployeeScheduleEditor = ({ employeeId, employeeName }) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [hasChanges, setHasChanges] = useState(false);

  // Recurring (non-weekly) pattern state
  const [recurrences, setRecurrences] = useState([]);
  const [recurrencesLoading, setRecurrencesLoading] = useState(true);
  const [addingRecurrence, setAddingRecurrence] = useState(false);
  const [newRecurrence, setNewRecurrence] = useState({
    day_of_week: 0,
    week_interval: 2,
    anchor_date: '',
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 30
  });

  // Initialize empty schedule
  const getEmptySchedule = () => {
    return DAYS.map((day, index) => ({
      day_of_week: index,
      day_name: day,
      is_working: false,
      start_time: '09:00',
      end_time: '17:00',
      break_minutes: 30,
    }));
  };

  // Normalise a schedule row from the API — handles null times and HH:MM:SS format
  const normaliseDay = (day) => ({
    ...day,
    start_time: day.start_time ? day.start_time.slice(0, 5) : '09:00',
    end_time: day.end_time ? day.end_time.slice(0, 5) : '17:00',
    break_minutes: day.break_minutes ?? 30,
  });

  // Fetch schedule
  useEffect(() => {
    const fetchSchedule = async () => {
      if (!employeeId) return;
      
      try {
        setLoading(true);
        const response = await api.get(`/employees/${employeeId}/schedule`);
        const raw = response.data.schedule || getEmptySchedule();
        setSchedule(raw.map(normaliseDay));
      } catch (error) {
        console.error('Error fetching schedule:', error);
        setSchedule(getEmptySchedule());
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [employeeId]);

  // Fetch recurring (non-weekly) patterns — separate endpoint, separate
  // loading state, since this is additive to the weekly schedule above
  // and most employees will have zero rows here.
  useEffect(() => {
    const fetchRecurrences = async () => {
      if (!employeeId) return;
      try {
        setRecurrencesLoading(true);
        const response = await api.get(`/employees/${employeeId}/schedule-recurrence`);
        setRecurrences(response.data.recurrences || []);
      } catch (error) {
        console.error('Error fetching schedule recurrences:', error);
        setRecurrences([]);
      } finally {
        setRecurrencesLoading(false);
      }
    };

    fetchRecurrences();
  }, [employeeId]);

  // Calculate hours for a day — always derived from current day values
  const calculateHours = (day) => {
    if (!day.is_working || !day.start_time || !day.end_time) return 0;
    
    const [startH, startM] = day.start_time.split(':').map(Number);
    const [endH, endM] = day.end_time.split(':').map(Number);
    
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const breakMins = Number(day.break_minutes) || 0;
    
    const totalMinutes = endMinutes - startMinutes - breakMins;
    return Math.max(0, Math.round(totalMinutes / 60 * 100) / 100);
  };

  // Derive weekly hours directly from schedule on every render
  const weeklyHours = Math.round(
    schedule.reduce((sum, day) => sum + calculateHours(day), 0) * 100
  ) / 100;

  // Update a field for a day
  const handleDayChange = (dayIndex, field, value) => {
    setSchedule(prev =>
      prev.map((day, idx) =>
        idx !== dayIndex ? day : { ...day, [field]: value }
      )
    );
    setHasChanges(true);
  };

  // Toggle working day
  const handleToggleWorking = (dayIndex) => {
    handleDayChange(dayIndex, 'is_working', !schedule[dayIndex].is_working);
  };

  // Save schedule
  const handleSave = async () => {
    try {
      setSaving(true);
      
      await api.put(`/employees/${employeeId}/schedule`, {
        schedule: schedule
      });
      
      setSnackbar({
        open: true,
        message: 'Schedule saved successfully',
        severity: 'success'
      });
      setHasChanges(false);
      
    } catch (error) {
      console.error('Error saving schedule:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to save schedule',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Add a recurrence
  const handleAddRecurrence = async () => {
    if (!newRecurrence.anchor_date) {
      setSnackbar({ open: true, message: 'Anchor date is required', severity: 'error' });
      return;
    }

    try {
      setAddingRecurrence(true);
      const response = await api.post(`/employees/${employeeId}/schedule-recurrence`, newRecurrence);
      setRecurrences(prev => [...prev, response.data.recurrence]);
      setSnackbar({ open: true, message: 'Recurrence added', severity: 'success' });
      // Reset just the anchor date, keeping the same interval/times for
      // convenience when adding a second day on the same pattern
      setNewRecurrence(prev => ({ ...prev, anchor_date: '' }));
    } catch (error) {
      console.error('Error adding recurrence:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to add recurrence',
        severity: 'error'
      });
    } finally {
      setAddingRecurrence(false);
    }
  };

  // Remove a recurrence
  const handleDeleteRecurrence = async (recurrenceId) => {
    try {
      await api.delete(`/employees/${employeeId}/schedule-recurrence/${recurrenceId}`);
      setRecurrences(prev => prev.filter(r => r.id !== recurrenceId));
      setSnackbar({ open: true, message: 'Recurrence removed', severity: 'success' });
    } catch (error) {
      console.error('Error deleting recurrence:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to remove recurrence',
        severity: 'error'
      });
    }
  };

  const handleNewRecurrenceChange = (field, value) => {
    setNewRecurrence(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight="600">
            Working Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set regular weekly working hours for {employeeName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<AccessTimeIcon />}
            label={`${weeklyHours} hrs/week`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Schedule'}
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have unsaved changes
        </Alert>
      )}

      {/* Schedule Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, width: 140 }}>Day</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 100 }} align="center">Working</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Break (mins)</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedule.map((day, index) => (
              <TableRow 
                key={day.day_of_week}
                sx={{ 
                  bgcolor: day.is_working ? 'inherit' : '#fafafa',
                  '&:hover': { bgcolor: day.is_working ? '#f5f5f5' : '#f0f0f0' }
                }}
              >
                <TableCell>
                  <Typography fontWeight={day.is_working ? 600 : 400}>
                    {day.day_name}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={day.is_working}
                    onChange={() => handleToggleWorking(index)}
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="time"
                    size="small"
                    value={day.start_time}
                    onChange={(e) => handleDayChange(index, 'start_time', e.target.value)}
                    disabled={!day.is_working}
                    sx={{ width: 130 }}
                    InputProps={{
                      sx: { bgcolor: day.is_working ? 'white' : 'transparent' }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="time"
                    size="small"
                    value={day.end_time}
                    onChange={(e) => handleDayChange(index, 'end_time', e.target.value)}
                    disabled={!day.is_working}
                    sx={{ width: 130 }}
                    InputProps={{
                      sx: { bgcolor: day.is_working ? 'white' : 'transparent' }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={day.break_minutes ?? 0}
                    onChange={(e) => handleDayChange(index, 'break_minutes', parseInt(e.target.value) || 0)}
                    disabled={!day.is_working}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 120, step: 5 }}
                    InputProps={{
                      sx: { bgcolor: day.is_working ? 'white' : 'transparent' }
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  {day.is_working ? (
                    <Typography fontWeight="600">
                      {calculateHours(day)} hrs
                    </Typography>
                  ) : (
                    <Typography color="text.secondary">Off</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Weekly Summary */}
      <Paper sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Working Days: {schedule.filter(d => d.is_working).length} days
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              Total Weekly Hours
            </Typography>
            <Typography variant="h5" fontWeight="700" color="primary">
              {weeklyHours} hours
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Recurring (Non-Weekly) Pattern */}
      <Paper sx={{ mt: 3, p: 2 }} variant="outlined">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <RepeatIcon color="action" />
          <Typography variant="h6" fontWeight="600">
            Recurring Pattern (Non-Weekly)
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this for working patterns that repeat every 2+ weeks rather than every week —
          for example, a 3-week rotating cycle. This is additive to the standard weekly
          schedule above, not a replacement for it.
        </Typography>

        {recurrencesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {recurrences.length > 0 && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Day</TableCell>
                      <TableCell>Every</TableCell>
                      <TableCell>Anchor Date</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell>Break</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recurrences.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{DAYS[r.day_of_week]}</TableCell>
                        <TableCell>{r.week_interval} week{r.week_interval > 1 ? 's' : ''}</TableCell>
                        <TableCell>{r.anchor_date}</TableCell>
                        <TableCell>{r.start_time?.slice(0, 5)}</TableCell>
                        <TableCell>{r.end_time?.slice(0, 5)}</TableCell>
                        <TableCell>{r.break_minutes} min</TableCell>
                        <TableCell align="right">{r.hours_worked} hrs</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleDeleteRecurrence(r.id)}>
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Day</InputLabel>
                <Select
                  label="Day"
                  value={newRecurrence.day_of_week}
                  onChange={(e) => handleNewRecurrenceChange('day_of_week', e.target.value)}
                >
                  {DAYS.map((day, idx) => (
                    <MenuItem key={idx} value={idx}>{day}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Every</InputLabel>
                <Select
                  label="Every"
                  value={newRecurrence.week_interval}
                  onChange={(e) => handleNewRecurrenceChange('week_interval', e.target.value)}
                >
                  <MenuItem value={2}>2 weeks</MenuItem>
                  <MenuItem value={3}>3 weeks</MenuItem>
                  <MenuItem value={4}>4 weeks</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Anchor Date"
                type="date"
                size="small"
                value={newRecurrence.anchor_date}
                onChange={(e) => handleNewRecurrenceChange('anchor_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="A date this pattern is definitely 'on'"
                sx={{ width: 180 }}
              />

              <TextField
                label="Start"
                type="time"
                size="small"
                value={newRecurrence.start_time}
                onChange={(e) => handleNewRecurrenceChange('start_time', e.target.value)}
                sx={{ width: 120 }}
              />

              <TextField
                label="End"
                type="time"
                size="small"
                value={newRecurrence.end_time}
                onChange={(e) => handleNewRecurrenceChange('end_time', e.target.value)}
                sx={{ width: 120 }}
              />

              <TextField
                label="Break (mins)"
                type="number"
                size="small"
                value={newRecurrence.break_minutes}
                onChange={(e) => handleNewRecurrenceChange('break_minutes', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 120, step: 5 }}
                sx={{ width: 110 }}
              />

              <Button
                variant="contained"
                startIcon={addingRecurrence ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                onClick={handleAddRecurrence}
                disabled={addingRecurrence}
              >
                Add
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeScheduleEditor;